import { useCallback, useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgPen, CgTrash } from "react-icons/cg";
import { Modal } from "react-bootstrap";
import CustomModal from "../Commaon/CustomModal";
import ImageWithLoader from "../Commaon/ImageWithLoader";
import { getWebpFileError } from "../../utils/fileValidation";
import {
  getRouteKeyFromLink,
  getRouteLinkLabel,
  getRouteLinkPayload,
  getRouteLinkValue,
  isContactRoute,
  routeOptions,
} from "../../utils/routeOptions";

const getSortPosition = (slider) => {
  const parsed = Number(slider.position ?? slider.order);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const getSortDate = (slider) => {
  const date = new Date(slider.updatedAt || slider.createdAt || 0).getTime();
  return Number.isNaN(date) ? 0 : date;
};

const sortSliders = (items) =>
  [...items].sort((a, b) => {
    const positionDiff = getSortPosition(a) - getSortPosition(b);
    if (positionDiff !== 0) return positionDiff;
    return getSortDate(b) - getSortDate(a);
  });

const SlidersList = () => {
  const [sliders, setSliders] = useState([]);
  const [filteredSliders, setFilteredSliders] = useState([]);
  const [addModel, setAddModel] = useState(false);
  const [editModel, setEditModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editLink, setEditLink] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [position, setPosition] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editId, setEditId] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [link, setLink] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [editContactNumber, setEditContactNumber] = useState("");
  const [image, setImage] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [editIsActive, setEditIsActive] = useState(true);
  const [errors, setErrors] = useState({});
  const [viewModel, setViewModel] = useState(false);
  const [viewId, setViewId] = useState(null);

  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const isWebLink = (value) =>
    typeof value === "string" && /^(https?:\/\/|\/)/i.test(value);

  const getSliders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/slider`, {
        method: "GET",
      });
      const data = response.data;
      if (data.s === 200) {
        const sortedSliders = sortSliders(data.r || []);
        setSliders(sortedSliders);
        setFilteredSliders(sortedSliders);
      }
    } catch (err) {
      console.error("Failed to fetch sliders:", err);
      setSnack({
        notification: { message: "Error fetching sliders", type: "error" },
      });
      setSliders([]);
      setFilteredSliders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getSliders();
  }, [getSliders]);

  // search filter
  useEffect(() => {
    if (!query.trim()) {
      setFilteredSliders(sliders);
    } else {
      setFilteredSliders(
        sortSliders(
          sliders.filter((c) =>
            c.title?.toLowerCase().includes(query.toLowerCase())
          )
        )
      );
    }
    setPageNo(1);
  }, [query, sliders]);

  // pagination slice
  const startIndex = (pageNo - 1) * limit;
  const paginatedSliders = filteredSliders.slice(
    startIndex,
    startIndex + limit
  );

  // action handlers
  const editSlider = (id) => {
    const slider = sliders.find((c) => c._id === id);
    if (slider) {
      setEditId(id);
      setTitle(slider.title);
      setEditTitle(slider.title);
      setEditDescription(slider.description);
      setEditLink(getRouteKeyFromLink(slider.link));
      setEditContactNumber(isContactRoute(getRouteKeyFromLink(slider.link)) ? getRouteLinkValue(slider.link) : "");
      setEditImage(null);
      setEditImagePreview(slider.imageUrl || "");
      setEditPosition(slider.position ?? slider.order ?? "");
      setEditIsActive(slider.isActive ?? true);
      setEditModel(true);
    }
  };

  const deleteSlider = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
  };

  const addSlider = () => {
    setIsActive(true);
    setAddModel(true);
  };

  const viewSlider = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slider/${id}`, {
        method: "GET",
      });
      if (response?.data?.r) {
        setSliders((prev) =>
          prev.map((slider) => (slider._id === id ? { ...slider, ...response.data.r } : slider))
        );
      }
      setViewId(id);
      setViewModel(true);
    } catch (err) {
      setViewId(id);
      setViewModel(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Validation for Add/Edit
  const validateAddForm = (isEdit = false) => {
    let newErrors = {};

    if (!isEdit && !title.trim()) {
      newErrors.title = "Slider title is required";
    }
    if (isEdit && !editTitle.trim()) {
      newErrors.title = "Slider title is required";
    }

    if (!isEdit && !description.trim()) {
      newErrors.description = "Description is required";
    }
    if (isEdit && !editDescription.trim()) {
      newErrors.description = "Description is required";
    }

    if (!isEdit && !link.trim()) {
      newErrors.link = "Link is required";
    }
    if (isEdit && !editLink.trim()) {
      newErrors.link = "Link is required";
    }
    const selectedRoute = isEdit ? editLink : link;
    const selectedNumber = isEdit ? editContactNumber : contactNumber;
    if (isContactRoute(selectedRoute) && !/^\d{10}$/.test(selectedNumber)) {
      newErrors.contactNumber = "Enter a valid 10-digit mobile number";
    }

    if (!isEdit && !image) {
      newErrors.image = "Image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSlider = async (e) => {
    e.preventDefault();
    if (!validateAddForm()) return;

    const formData = new FormData();
    formData.append("title", title);
    formData.append("link", JSON.stringify(getRouteLinkPayload(link, contactNumber)));
    formData.append("description", description);
    const fileError = getWebpFileError(image);
    if (fileError) {
      setErrors((prev) => ({ ...prev, image: fileError }));
      return;
    }
    if (image) formData.append("sliderImage", image);
    if (position !== "") formData.append("position", position);
    formData.append("isActive", String(isActive));

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slider`, {
        method: "POST",
        body: formData,
      });
      const data = response.data;

      if (data.s === 201) {
        setSliders((prev) => sortSliders([...prev, data.r]));
        setFilteredSliders((prev) => sortSliders([...prev, data.r]));
        setSnack({
          notification: {
            message: "Slider added successfully",
            type: "success",
          },
        });
        setAddModel(false);
        setTitle("");
        setLink("");
        setContactNumber("");
        setDescription("");
        setImage(null);
        setPosition("");
        setIsActive(true);
        setErrors({});
      } else {
        throw new Error(data.m || "Failed to add Slider");
      }
    } catch (err) {
      console.error("Error adding Slider:", err);
      setSnack({
        notification: {
          message: err.message || "Error adding Slider",
          type: "error",
        },
      });
    } finally {
      getSliders();
      setLoading(false);
    }
  };

  const handleEditSlider = async (e) => {
    e.preventDefault();
    if (!validateAddForm(true)) return;

    const formData = new FormData();
    formData.append("title", editTitle);
    formData.append("link", JSON.stringify(getRouteLinkPayload(editLink, editContactNumber)));
    formData.append("description", editDescription);
    const fileError = getWebpFileError(editImage);
    if (fileError) {
      setErrors((prev) => ({ ...prev, image: fileError }));
      return;
    }
    if (editImage) formData.append("sliderImage", editImage);
    formData.append("position", editPosition);
    formData.append("isActive", String(editIsActive));

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slider/${editId}`, {
        method: "PUT",
        body: formData,
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Slider updated successfully",
            type: "success",
          },
        });
      } else {
        setSnack({
          notification: {
            message: data.m || "Error updating Slider",
            type: "error",
          },
        });
      }
    } catch (err) {
      console.error("Error updating Slider:", err);
      setSnack({
        notification: {
          message: err.message || "Error updating Slider",
          type: "error",
        },
      });
    } finally {
      getSliders();
      setLoading(false);
      setEditModel(false);
      setEditTitle("");
      setEditDescription("");
      setEditId(null);
      setEditImage(null);
      setEditImagePreview("");
      setEditPosition("");
      setEditLink("");
      setEditContactNumber("");
      setEditIsActive(true);
      setTitle("");
      setErrors({});
    }
  };

  const handleDeleteSlider = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slider/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Slider deleted successfully",
            type: "success",
          },
        });
      }
    } catch (err) {
      console.error("Error deleting Slider:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting Slider",
          type: "error",
        },
      });
    } finally {
      getSliders();
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  const toggleSliderStatus = async (slider) => {
    const nextStatus = !slider.isActive;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slider/${slider._id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: nextStatus }),
      });
      const data = response.data;

      if (data.s === 200) {
        setSliders((prev) =>
          sortSliders(
            prev.map((item) =>
              item._id === slider._id ? { ...item, isActive: nextStatus } : item
            )
          )
        );
        setSnack({
          notification: { message: "Slider status updated", type: "success" },
        });
      } else {
        throw new Error(data.m || "Error updating slider status");
      }
    } catch (err) {
      setSnack({
        notification: { message: err.message || "Error updating slider status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}
      <div className="card shadow-sm border-0 rounded-3 p-3">
        {/* Header + Page Size */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Sliders</h2>
          <div>
            <select
              className="form-select form-select-sm "
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control"
              style={{ width: "40vw" }}
              placeholder="Search sliders..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn-outline-custom">Search</button>
          </div>
          <button
            className="btn btn-outline-custom"
            onClick={() => addSlider()}
          >
            Add Slider
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table
            className="table table-hover align-middle"
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <thead className="table-light">
              <tr>
                <th>Sr.</th>
                <th>Slider Title</th>
                <th>Link</th>
                <th>Position</th>
                <th>Image</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedSliders.length > 0 &&
                paginatedSliders.map((slider, idx) => (
                  <tr key={slider._id || idx}>
                    <td>{startIndex + idx + 1}</td>
                    <td>{slider.title || "-"}</td>
                    <td>
                      {slider.link ? (
                        isWebLink(getRouteLinkValue(slider.link)) ? (
                          <a
                            href={getRouteLinkValue(slider.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none text-truncate d-inline-block"
                            style={{ maxWidth: "220px" }}
                            title={getRouteLinkValue(slider.link)}
                          >
                            {getRouteLinkLabel(slider.link)}
                          </a>
                        ) : (
                          <span className="text-dark">{getRouteLinkLabel(slider.link)}</span>
                        )
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{slider.position ?? slider.order ?? "-"}</td>

                    <td>
                      {slider.imageUrl ? (
                        <ImageWithLoader
                          src={slider.imageUrl}
                          alt={slider.title}
                          style={{ width: "40px", height: "40px" }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {slider.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleSliderStatus(slider)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleSliderStatus(slider)}>Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-row justify-content-start gap-2">
                        <button
                          className="btn btn-sm btn-outline-custom "
                          onClick={() => viewSlider(slider._id)}
                        >
                          <CgEye size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-custom "
                          onClick={() => editSlider(slider._id)}
                        >
                          <CgPen size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteSlider(slider._id)}
                        >
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedSliders.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No sliders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {Math.ceil(filteredSliders.length / limit) || 1}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() =>
              setPageNo((prev) =>
                prev < Math.ceil(filteredSliders.length / limit)
                  ? prev + 1
                  : prev
              )
            }
            disabled={pageNo >= Math.ceil(filteredSliders.length / limit)}
          >
            Next
          </button>
        </div>
      </div>
      {/* Add Slider Modal */}
      <Modal show={addModel} onHide={() => setAddModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Slider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddSlider}>
            <div className="mb-3">
              <label className="form-label">Slider Title</label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className={`form-control ${
                  errors.description ? "is-invalid" : ""
                }`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Link</label>
              <select
                className={`form-select ${errors.link ? "is-invalid" : ""}`}
                value={link}
                onChange={(e) => { const value = e.target.value; setLink(value); if (!isContactRoute(value)) setContactNumber(""); }}
                disabled={loading}
              >
                <option value="" disabled>
                  Select redirection route
                </option>
                {routeOptions.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.key}
                  </option>
                ))}
              </select>
              {errors.link && (
                <div className="invalid-feedback">{errors.link}</div>
              )}
            </div>
            {isContactRoute(link) && (
              <div className="mb-3">
                <label className="form-label">Mobile Number</label>
                <input type="tel" inputMode="numeric" maxLength={10} className={`form-control ${errors.contactNumber ? "is-invalid" : ""}`} value={contactNumber} onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} disabled={loading} />
                {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Icon</label>
              <input
                type="file"
                className={`form-control ${errors.image ? "is-invalid" : ""}`}
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setImage(fileError ? null : file);
                  setErrors((prev) => ({ ...prev, image: fileError }));
                }}
                disabled={loading}
              />
              {errors.image && (
                <div className="invalid-feedback">{errors.image}</div>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Add Slider"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* Edit Slider Modal */}
      <Modal show={editModel} onHide={() => setEditModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Slider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditSlider}>
            <div className="mb-3">
              <div className="mb-3">Current Title: {title}</div>
              <label className="form-label">Slider Title</label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={loading}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className={`form-control ${
                  errors.description ? "is-invalid" : ""
                }`}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                disabled={loading}
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Link</label>
              <select
                className={`form-select ${errors.link ? "is-invalid" : ""}`}
                value={editLink}
                onChange={(e) => { const value = e.target.value; setEditLink(value); if (!isContactRoute(value)) setEditContactNumber(""); }}
                disabled={loading}
              >
                <option value="" disabled>
                  Select redirection route
                </option>
                {routeOptions.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.key}
                  </option>
                ))}
              </select>
              {errors.link && (
                <div className="invalid-feedback">{errors.link}</div>
              )}
            </div>
            {isContactRoute(editLink) && (
              <div className="mb-3">
                <label className="form-label">Mobile Number</label>
                <input type="tel" inputMode="numeric" maxLength={10} className={`form-control ${errors.contactNumber ? "is-invalid" : ""}`} value={editContactNumber} onChange={(e) => setEditContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} disabled={loading} />
                {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={editPosition}
                onChange={(e) => setEditPosition(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={editIsActive ? "true" : "false"}
                onChange={(e) => setEditIsActive(e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Icon</label>
              <input
                type="file"
                className="form-control"
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setEditImage(fileError ? null : file);
                  setErrors((prev) => ({ ...prev, image: fileError }));
                }}
                disabled={loading}
              />
              {errors.image && (
                <div className="invalid-feedback d-block">{errors.image}</div>
              )}
              {editImagePreview && !editImage && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Current Image</div>
                  <ImageWithLoader
                    src={editImagePreview}
                    alt={editTitle}
                    style={{ width: "96px", height: "54px", objectFit: "cover" }}
                  />
                </div>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Update Slider"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* View Slider Modal */}
      <Modal
        show={viewModel}
        onHide={() => setViewModel(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="fw-semibold fs-6">
            Slider Information
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 pb-4">
          {viewId ? (
            (() => {
              const slider = sliders.find((c) => c._id === viewId);

              return (
                <div className="d-flex flex-column gap-3">
                  {/* Image */}
                  <div className="d-flex justify-content-start">
                    {slider?.imageUrl ? (
                      <div
                        className="border rounded-3 overflow-hidden"
                        style={{ maxWidth: "460px", maxHeight: "220px" }}
                      >
                        <ImageWithLoader
                          src={slider.imageUrl}
                          alt="Slider"
                          className="w-100"
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                    ) : (
                      <span className="text-muted small">
                        No image available
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="border rounded-3 p-3">
                    <div className="mb-2">
                      <div className="text-muted small">Title</div>
                      <div className="fw-medium">{slider?.title || "-"}</div>
                    </div>

                    <div className="mb-2">
                      <div className="text-muted small">Description</div>
                      <div className="text-secondary small">
                        {slider?.description || "-"}
                      </div>
                    </div>

                    <div>
                      <div className="text-muted small">Redirect URL</div>
                      {slider?.link ? (
                        isWebLink(getRouteLinkValue(slider.link)) ? (
                          <a
                            href={getRouteLinkValue(slider.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none small text-break"
                          >
                            {getRouteLinkLabel(slider.link)}
                          </a>
                        ) : (
                          <span className="small text-dark">{getRouteLinkLabel(slider.link)}</span>
                        )
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <p className="text-center text-muted small">
              No slider information available
            </p>
          )}
        </Modal.Body>
      </Modal>
      {/* Delete Confirm */}
      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message="Are you sure you want to delete this slider?"
        onConfirm={() => {
          handleDeleteSlider(deleteId);
          setDeleteModel(false);
        }}
        onCancel={() => setDeleteModel(false)}
      />
    </>
  );
};

export default SlidersList;
